<template>
    <div>
        <b-badge
            v-if="paused"
            variant="light"
            v-b-tooltip.hover
            class="border text-secondary"
            pill
            :title="$t('Billing is paused while video is disconnected')"
        >
            <font-awesome-icon icon="fa-duotone fa-pause-circle" />
            Billing Paused
        </b-badge>
        <b-badge
            v-else-if="isFreeTime"
            variant="light"
            v-b-tooltip.hover
            class="border text-primary"
            pill
            :title="$t(`The first ${freeSeconds} seconds are free`)"
        >
            <font-awesome-icon icon="fa-duotone fa-info-circle" />
            Free Time
            {{ countdown }}
        </b-badge>
        <coin-badge
            v-else-if="currentCall.totalEarnings"
            :amount="currentCall.totalEarnings"
            :label="onboardingUser.creator ? $t('Earnings') : $t('Charges')"
        />
    </div>
</template>

<script>
import { mapState } from "vuex";

import { FREE_TIME_SECONDS } from "@/constant/call";

const TICK_MS = 1000;
const CHARGE_INTERVAL_MS = 60000;
// cap per-tick credit so a frozen / suspended tab can never dump
// minutes of "airtime" into one tick on wake-up
const MAX_TICK_ELAPSED_MS = 5000;

// timing math runs off performance.now(), not tick counts. setInterval
// is "best effort, no sooner than" — under WebRTC load it consistently
// fires late, so counting ticks under-bills. crediting actual elapsed
// wallclock per tick is throttle-proof.
export default {
    data() {
        return {
            tickInterval: null,
            lastTickAt: null,
            airtimeMs: 0,
            msUntilNextCharge: 0,
            freeSeconds: FREE_TIME_SECONDS,
        };
    },
    computed: {
        ...mapState("call", [
            "currentCall",
            "disableFreeTime",
            "hasRemoteVideoTrack",
            "isCameraOn",
        ]),
        ...mapState({
            onboardingUser: (state) => state.onboarding.user,
        }),
        // billing tracks the creator's video — that's the only stream that
        // matters. customers don't need a camera. so on the creator's browser
        // that's her own camera (isCameraOn); on the customer's browser
        // that's the creator's stream coming in (hasRemoteVideoTrack).
        paused() {
            return this.onboardingUser.creator
                ? !this.isCameraOn
                : !this.hasRemoteVideoTrack;
        },
        airtimeSeconds() {
            return Math.floor(this.airtimeMs / 1000);
        },
        isFreeTime() {
            if (this.disableFreeTime) return false;
            return this.airtimeSeconds < this.freeSeconds;
        },
        countdown() {
            return Math.max(0, this.freeSeconds - this.airtimeSeconds);
        },
    },
    created() {
        this.msUntilNextCharge = this.disableFreeTime
            ? 0
            : this.freeSeconds * 1000;
    },
    mounted() {
        this.lastTickAt = performance.now();
        this.tickInterval = setInterval(this.tick, TICK_MS);
    },
    methods: {
        tick() {
            const now = performance.now();
            const elapsed = Math.min(now - this.lastTickAt, MAX_TICK_ELAPSED_MS);
            this.lastTickAt = now;

            if (this.paused) return;

            this.airtimeMs += elapsed;
            this.$store.commit(
                "call/setAirtimeSeconds",
                Math.floor(this.airtimeMs / 1000)
            );

            // creators don't drive the billing dispatch — only the paying
            // side fires charges.
            if (this.onboardingUser.creator) return;

            this.msUntilNextCharge -= elapsed;
            if (this.msUntilNextCharge <= 0) {
                this.$store.dispatch("call/charge");
                this.msUntilNextCharge = CHARGE_INTERVAL_MS;
            }
        },
    },
    beforeDestroy() {
        if (this.tickInterval) clearInterval(this.tickInterval);
    },
};
</script>
